import { useRef } from 'react';
import type { DocumentData } from '@sdk/users/types/DocumentData';
import { TableRow, TableCell } from '@sdk/components/ui/table';
import dateToHumanFormat from '@sdk/utils/dateFormat/dateToHumanFormat';
import MoreOptionsMenu from '@sdk/components/ui/MoreOptionsMenu';
import getRequestUrl from '@sdk/api/utils/getRequestUrl';
import gettext from '@sdk/lang/gettext';
import Text14 from '@sdk/components/ui/typography/Text14';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';

import BodyPortal from '@web/components/BodyPortal';
import { DeleteConfirmModal } from '@web/components/DeleteConfirmModal/DeleteConfirmModal';
import FormSubmit from '@web/components/FormSubmit';
import DocumentPreview from '@web/users/documents/DocumentPreview/DocumentPreview';
import { useDocumentListItem } from '@web/users/documents/DocumentListItem/documentListItem.hooks';

type Props = {
  data: DocumentData;
  onRemoved: () => void;
  onEdit: (document: DocumentData) => void;
};

const DocumentListItem = ({ data, onRemoved, onEdit }: Props) => {
  const url = getRequestUrl(`users/${data.user.id}/documents/${data.id}/download`, {});
  const downloadFormRef = useRef(null);
  const documentPreviewRef = useRef(null);

  const { moreOptions, onOptionClick, deleteConfirmationModalState, remove } = useDocumentListItem(
    data,
    onRemoved,
    onEdit,
    downloadFormRef,
    documentPreviewRef
  );

  return (
    <>
      <TableRow wholeClickable onClick={() => onEdit(data)}>
        <TableCell>{data.name}</TableCell>
        <TableCell>{data.description || '-'}</TableCell>
        <TableCell>
          {data.expirationDate ? (
            <>
              {data.isExpiringSoon && (
                <Text14 themeColor={ThemeColorType.warning}>
                  {gettext('Expires on %(when)s', {
                    when: dateToHumanFormat(data.expirationDate, false, true),
                  })}
                </Text14>
              )}
              {data.isExpired && (
                <Text14 themeColor={ThemeColorType.danger}>
                  {gettext('Expired on %(when)s', {
                    when: dateToHumanFormat(data.expirationDate, false, true),
                  })}
                </Text14>
              )}
              {!data.isExpiringSoon && !data.isExpired && (
                <Text14 themeColor={ThemeColorType.gray}>
                  {dateToHumanFormat(data.expirationDate, false, true)}
                </Text14>
              )}
            </>
          ) : (
            '-'
          )}
        </TableCell>
        <TableCell>{data.lastModifiedBy.fullname}</TableCell>
        <TableCell pTop={0} pBottom={0} alignRight>
          <MoreOptionsMenu
            noBorder
            options={moreOptions}
            onClick={(option): void => onOptionClick(option)}
          />
        </TableCell>
      </TableRow>
      <FormSubmit formRef={downloadFormRef} url={url} />
      <DocumentPreview document={data} ref={documentPreviewRef} />
      <BodyPortal>
        <DeleteConfirmModal
          modalState={deleteConfirmationModalState}
          onSubmit={remove}
          title={gettext('Delete document')}
          description={gettext('You are about to delete this document.')}
          textToRetype={gettext('DELETE')}
        />
      </BodyPortal>
    </>
  );
};

export default DocumentListItem;
